import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Clock, CheckCircle2, XCircle } from "lucide-react";
import { SCField, SCSection } from "./Common";
import { scAdminSettings, scAdminSaveSettings, scAdminSchedulerStatus, scAdminSchedulerReload } from "../../../lib/sifcontrolApi";

export default function SettingsTab() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);

    const loadStatus = () => scAdminSchedulerStatus().then(setStatus).catch(() => setStatus(null));

    useEffect(() => {
        scAdminSettings().then(setData).catch(() => toast.error("Could not load settings"));
        loadStatus();
    }, []);

    if (!data) return <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading…</div>;

    const setIntg = (k, v) => setData({ ...data, integrations: { ...(data.integrations || {}), [k]: v } });
    const setSched = (k, v) => setData({ ...data, scheduler: { ...(data.scheduler || {}), [k]: v } });

    const save = async () => {
        setSaving(true);
        try {
            await scAdminSaveSettings({
                risk_free_rate: Number(data.risk_free_rate),
                disclaimer: data.disclaimer,
                ranking_disclaimer: data.ranking_disclaimer,
                integrations: data.integrations,
                scheduler: data.scheduler,
                universe: data.universe,
            });
            toast.success("Settings saved");
            // reload scheduler + settings so the status card refreshes
            try { await scAdminSchedulerReload(); } catch { /* logged server-side */ }
            loadStatus();
            const fresh = await scAdminSettings();
            setData(fresh);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const integrations = data.integrations || {};

    return (
        <div data-testid="sc-admin-settings">
            <SCSection
                title="SIF Control · Settings"
                subtitle="Analytics parameters, disclaimers, integrations and scheduler."
                right={
                    <button onClick={save} disabled={saving} className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3" data-testid="sc-settings-save">
                        <Save size={12} /> {saving ? "Saving…" : "Save changes"}
                    </button>
                }
                testId="sc-settings-header"
            />

            <div className="space-y-6">
                <div className="rounded-2xl border border-oth-line/70 bg-white p-5">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-3">Analytics</div>
                    <SCField
                        label="Risk-free rate (% annualised)"
                        value={data.risk_free_rate}
                        onChange={(v) => setData({ ...data, risk_free_rate: v })}
                        type="number"
                        testId="sc-settings-riskfree"
                        hint="Used in Sharpe/Sortino calculations. Indian G-Sec proxy — configurable."
                    />
                </div>

                <div className="rounded-2xl border border-oth-line/70 bg-white p-5">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-3">Browserless (AMFI ingestion)</div>
                    <SCField
                        label="Browserless token"
                        value={integrations.browserless_token || ""}
                        onChange={(v) => setIntg("browserless_token", v)}
                        testId="sc-settings-browserless-token"
                        hint="AMFI SIF pages are JS-rendered — required for ingestion. Stored server-side and masked on subsequent loads. Get one at browserless.io."
                        type="password"
                    />
                    <SCField
                        label="Browserless URL"
                        value={integrations.browserless_url || "https://chrome.browserless.io"}
                        onChange={(v) => setIntg("browserless_url", v)}
                        testId="sc-settings-browserless-url"
                        hint="Default hosted endpoint. Change only if self-hosting."
                    />
                </div>

                <div className="rounded-2xl border border-oth-line/70 bg-white p-5">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-3">Integrations</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <SCField label="Google reCAPTCHA site key" value={integrations.recaptcha_site_key || ""} onChange={(v) => setIntg("recaptcha_site_key", v)} testId="sc-settings-recaptcha-site" />
                        <SCField label="Google reCAPTCHA secret" value={integrations.recaptcha_secret_key || ""} onChange={(v) => setIntg("recaptcha_secret_key", v)} testId="sc-settings-recaptcha-secret" type="password" />
                        <SCField label="Gmail client ID" value={integrations.gmail_client_id || ""} onChange={(v) => setIntg("gmail_client_id", v)} testId="sc-settings-gmail-id" />
                        <SCField label="Gmail client secret" value={integrations.gmail_client_secret || ""} onChange={(v) => setIntg("gmail_client_secret", v)} testId="sc-settings-gmail-secret" type="password" />
                        <SCField label="Gmail sender email" value={integrations.gmail_sender_email || ""} onChange={(v) => setIntg("gmail_sender_email", v)} testId="sc-settings-gmail-sender" type="email" />
                        <SCField label="Google Analytics measurement ID" value={integrations.google_analytics_id || ""} onChange={(v) => setIntg("google_analytics_id", v)} testId="sc-settings-ga-id" placeholder="G-XXXXXXX" />
                    </div>
                </div>

                <div className="rounded-2xl border border-oth-line/70 bg-white p-5" data-testid="sc-settings-universe">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-2">Universe — global calculation & display filter</div>
                    <div className="text-[12px] font-body text-oth-ink3 mb-4">
                        Pick which plan × option combinations are used across the SIF list, screener, rankings and every analytics recompute. Un-ticking everything = show all.
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { grp: "plans", label: "Regular plan", value: "Regular" },
                            { grp: "plans", label: "Direct plan", value: "Direct" },
                            { grp: "options", label: "Growth option", value: "Growth" },
                            { grp: "options", label: "IDCW option", value: "IDCW" },
                        ].map((c) => {
                            const cur = new Set(data.universe?.[c.grp] || []);
                            const checked = [...cur].some((v) => (v || "").toLowerCase() === c.value.toLowerCase());
                            return (
                                <label key={c.label} className="flex items-center gap-2 rounded-xl border border-oth-line/70 px-3 py-2.5">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                            const next = new Set(cur);
                                            if (e.target.checked) next.add(c.value); else {
                                                for (const v of [...next]) if ((v || "").toLowerCase() === c.value.toLowerCase()) next.delete(v);
                                            }
                                            setData({ ...data, universe: { ...(data.universe || {}), [c.grp]: [...next] } });
                                        }}
                                        className="w-4 h-4"
                                        data-testid={`sc-settings-universe-${c.value.toLowerCase()}`}
                                    />
                                    <span className="text-xs font-body text-oth-ink">{c.label}</span>
                                </label>
                            );
                        })}
                    </div>
                    <div className="mt-3 text-[11px] font-body text-oth-ink3">
                        Saving here triggers a full analytics + rankings recompute so downstream data stays consistent with the chosen universe.
                    </div>
                </div>

                <div className="rounded-2xl border border-oth-line/70 bg-white p-5" data-testid="sc-settings-scheduler">
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body">Scheduler</div>
                            <div className="text-[12px] font-body text-oth-ink3 mt-1">Enable automated NAV ingestion at your preferred IST time. AUM/TER come from admin XLSX uploads.</div>
                        </div>
                        <div className="text-[11px] font-body text-oth-ink3 flex items-center gap-1.5">
                            {status?.running ? <CheckCircle2 size={12} className="text-green-700" /> : <XCircle size={12} className="text-red-700" />}
                            {status?.running ? "Running" : "Stopped"}
                            {status?.jobs?.length > 0 && (
                                <span className="ml-2"><Clock size={11} className="inline mr-0.5" /> Next: {status.jobs[0].next_run_time ? new Date(status.jobs[0].next_run_time).toLocaleString("en-IN") : "—"}</span>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <label className="flex items-center gap-2 rounded-xl border border-oth-line/70 px-3 py-2.5">
                            <input
                                type="checkbox"
                                checked={!!data.scheduler?.nav_enabled}
                                onChange={(e) => setSched("nav_enabled", e.target.checked)}
                                className="w-4 h-4"
                                data-testid="sc-settings-nav-enabled"
                            />
                            <span className="text-xs font-body text-oth-ink">Enable daily NAV cron</span>
                        </label>
                        <SCField
                            label="NAV time (IST, HH:MM)"
                            value={data.scheduler?.nav_time_ist || "22:00"}
                            onChange={(v) => setSched("nav_time_ist", v)}
                            testId="sc-settings-nav-time"
                            placeholder="22:00"
                        />
                        <SCField
                            label="Timezone"
                            value={data.scheduler?.timezone || "Asia/Kolkata"}
                            onChange={(v) => setSched("timezone", v)}
                            testId="sc-settings-timezone"
                        />
                    </div>
                    <div className="mt-3 text-[11px] font-body text-oth-ink3">
                        Saving these settings automatically reloads the scheduler — the next NAV run is scheduled for the new time.
                    </div>
                </div>

                <div className="rounded-2xl border border-oth-line/70 bg-white p-5">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-3">Disclaimers</div>
                    <SCField label="Product disclaimer" value={data.disclaimer} onChange={(v) => setData({ ...data, disclaimer: v })} type="textarea" testId="sc-settings-disclaimer" />
                    <SCField label="Ranking disclaimer" value={data.ranking_disclaimer} onChange={(v) => setData({ ...data, ranking_disclaimer: v })} type="textarea" testId="sc-settings-rank-disclaimer" />
                </div>
            </div>
        </div>
    );
}
